import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Users } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, formatApiError } from "../../lib/api";
import { orgName } from "../../lib/labels";
import { SKILLS } from "../public/ManagerRegistration";
import { MyNotifications, ProjectRequestPanel, useProjectRequests } from "../../components/ManagerRequests";

const B = process.env.REACT_APP_BACKEND_URL;
const COFFEE = { ROBUSTA: "Robusta", ARABIKA: "Arabika", LIBERIKA: "Liberika", LAINNYA: "Lainnya" };
const MAX_COMPARE = 3;
const ROWS = [
  ["Pengalaman", (c) => `${c.yearsExperience} tahun`],
  ["Spesialisasi", (c) => (c.coffeeTypes || []).map((x) => COFFEE[x]).join(", ") || "-"],
  ["Wilayah kerja", (c) => c.workRegion || "-"],
  ["Kebun terbesar", (c) => (c.largestFarmHa != null ? `${c.largestFarmHa} ha` : "-")],
  ["Kapasitas", (c) => `${c.maxHectares} ha · ${c.maxActiveProjects} kebun`],
  ["Sedang dikelola", (c) => `${c.activeHectares} ha · ${c.activeProjects} kebun`],
  ["Sisa kapasitas", (c) => `${c.remainingHectares} ha · ${c.remainingProjects} kebun`],
  ["Tim lapangan", (c) => (c.hasTeam ? `Ya (${c.teamSize})` : "Tidak")],
  ["Keahlian", (c) => (c.skills || []).map((s) => SKILLS[s] || s).join(", ") || "-"],
  ["Rating", (c) => c.rating ?? "Belum ada"],
  ["Kecocokan kebun", (c) => (c.selectable ? "Bisa diajukan" : c.reasons.join(" · "))],
];

const Avatar = ({ c }) => (c.photoUrl
  ? <img src={`${B}${c.photoUrl}`} alt="" className="h-12 w-12 rounded-full object-cover border" />
  : <span className="h-12 w-12 shrink-0 rounded-full bg-emerald-100 grid place-items-center"><UserCheck className="h-5 w-5 text-emerald-800" /></span>);

const ProposeBtn = ({ c, onPick, busy, locked, testid }) => (c.current
  ? <p className="text-xs font-bold text-emerald-800" data-testid={`${testid}-current`}>Pengelola kebun ini</p>
  : <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" disabled={!c.selectable || busy || !!locked} onClick={() => onPick("INDIVIDUAL_MANAGER", c.id, c.fullName)} data-testid={testid}>
    {c.capacityFull ? "Kapasitas Penuh" : "Ajukan sebagai Pengelola"}</Button>);

const CandidateCard = ({ c, checked, onToggle, canAdd, ...rest }) => (
  <div className={`rounded-xl border p-4 text-sm space-y-3 ${c.current ? "border-emerald-600 bg-emerald-50" : "bg-white"}`} data-testid={`inv-mgr-card-${c.id}`}>
    <div className="flex items-center gap-3"><Avatar c={c} />
      <div className="min-w-0 flex-1"><p className="font-bold text-slate-900 truncate">{c.fullName}</p>
        <p className="text-xs text-slate-500">{c.workRegion || "-"} · {c.yearsExperience} th pengalaman</p></div></div>
    <p className="text-xs text-slate-600 line-clamp-3">{c.profileSummary}</p>
    <p className="text-xs"><b>Sisa kapasitas:</b> {c.remainingHectares} ha · {c.remainingProjects} kebun</p>
    {c.reasons.length > 0 && <p className="text-xs font-bold text-red-700" data-testid={`inv-mgr-reason-${c.id}`}>{c.reasons.join(" · ")}</p>}
    <div className="flex flex-wrap items-center justify-between gap-2">
      <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
        <Checkbox checked={checked} disabled={!checked && !canAdd} onCheckedChange={() => onToggle(c.id)} data-testid={`inv-mgr-compare-${c.id}`} />Bandingkan</label>
      <ProposeBtn c={c} {...rest} testid={`inv-mgr-propose-${c.id}`} />
    </div>
  </div>
);

const CompareTable = ({ list, ...rest }) => (
  <div className="bg-white border rounded-xl overflow-x-auto" data-testid="inv-mgr-compare-table">
    <table className="w-full min-w-[560px] text-sm">
      <thead><tr className="border-b bg-slate-50">
        <th className="p-3 text-left text-xs text-slate-500 w-36">Perbandingan</th>
        {list.map((c) => <th key={c.id} className="p-3 text-left"><div className="flex items-center gap-2"><Avatar c={c} /><span className="font-bold">{c.fullName}</span></div></th>)}
      </tr></thead>
      <tbody>
        {ROWS.map(([k, fn]) => <tr key={k} className="border-b"><td className="p-3 text-xs text-slate-500">{k}</td>
          {list.map((c) => <td key={c.id} className="p-3 font-medium text-slate-900">{fn(c)}</td>)}</tr>)}
        <tr><td className="p-3" />{list.map((c) => <td key={c.id} className="p-3"><ProposeBtn c={c} {...rest} testid={`inv-mgr-compare-propose-${c.id}`} /></td>)}</tr>
      </tbody>
    </table>
  </div>
);

const GroupOption = ({ o, onPick, busy, locked }) => {
  const g = o.farmerGroup;
  if (!g) return null;
  const cur = o.current.managerType === "FARMER_GROUP";
  return (
    <div className={`rounded-xl border p-4 text-sm flex flex-wrap items-center justify-between gap-3 ${cur ? "border-emerald-600 bg-emerald-50" : "bg-white"}`} data-testid="inv-mgr-group">
      <span className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-800" /><b>{orgName(g)}</b><span className="text-xs text-slate-500">Kelompok Tani/Koperasi pada lahan ini</span></span>
      {cur ? <span className="text-xs font-bold text-emerald-800">Pengelola kebun ini</span>
        : <Button size="sm" variant="outline" disabled={!g.selectable || busy || !!locked} onClick={() => onPick("FARMER_GROUP", null, orgName(g))} data-testid="inv-mgr-group-propose">Ajukan Kelompok/Koperasi</Button>}
    </div>
  );
};

export default function InvestorManagers() {
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState("");
  const [o, setO] = useState(null);
  const [cmp, setCmp] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/projects").then((r) => { setProjects(r.data); if (r.data[0]) setPid(r.data[0].id); }).catch(() => setProjects([])); }, []);
  const load = useCallback(() => { if (pid) { api.get(`/projects/${pid}/manager-options`).then((r) => setO(r.data)).catch((e) => { setO(false); toast.error(formatApiError(e)); }); } }, [pid]);
  useEffect(() => { setO(null); setCmp([]); load(); }, [load]);
  const req = useProjectRequests(pid);
  const toggle = (id) => setCmp((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < MAX_COMPARE ? [...s, id] : s));
  const onPick = async (managerType, managerId, name) => {
    if (!window.confirm(`Ajukan ${name} sebagai pengelola kebun ini? Pengelola harus menerima, lalu PT SJI menyetujui sebelum resmi ditetapkan.`)) return;
    setBusy(true);
    try { await api.put(`/projects/${pid}/manager`, { managerType, managerId }); toast.success("Pengajuan dikirim — menunggu jawaban Pengelola"); load(); req.reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const list = o ? o.candidates.filter((c) => cmp.includes(c.id)) : [];
  const shared = { onPick, busy, locked: o?.locked || (req.active ? "Ada pengajuan sedang diproses" : null) };
  return (
    <div className="space-y-5" data-testid="investor-managers-page">
      <div><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Pilihan Pengelola</h1>
        <p className="text-sm text-slate-600 mt-1">Bandingkan pengelola terverifikasi PT SJI lalu ajukan kandidat untuk kebun kopi Anda. Pengelola yang diajukan harus menerima, lalu PT SJI menyetujui sebelum resmi ditetapkan.</p></div>
      <MyNotifications testid="inv-notifications" />
      {projects && !projects.length && <p className="rounded-xl border bg-white p-5 text-sm text-slate-600" data-testid="inv-mgr-no-projects">Anda belum memiliki kebun. Pilihan pengelola tersedia setelah Anda memiliki kebun kopi.</p>}
      {projects?.length > 0 && (
        <div className="max-w-md"><p className="text-xs font-bold text-slate-500 mb-1">Rencana Kebun / Kebun</p>
          <Select value={pid} onValueChange={setPid}><SelectTrigger data-testid="inv-mgr-project-select"><SelectValue /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`inv-mgr-project-${p.id}`}>{p.projectCode} — {p.name}</SelectItem>)}</SelectContent></Select></div>)}
      {o && (
        <>
          <p className="text-xs text-slate-500" data-testid="inv-mgr-project-info">Luas kebun {o.projectHectares} ha · wilayah lahan {o.landRegion.regency || "-"}, {o.landRegion.province || "-"}</p>
          {shared.locked && <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="inv-mgr-locked">{o.locked || "Ada pengajuan pengelola yang sedang diproses. Tunggu hasilnya atau batalkan dulu sebelum mengajukan yang lain."}</p>}
          <ProjectRequestPanel rows={req.rows} onChanged={() => { req.reload(); load(); }} />
          <GroupOption o={o} onPick={onPick} busy={busy} locked={shared.locked} />
          {cmp.length > 0 && (
            <div className="space-y-2"><div className="flex items-center justify-between"><p className="text-sm font-bold">Perbandingan ({cmp.length}/{MAX_COMPARE})</p>
              <button type="button" className="text-xs text-slate-500 underline" onClick={() => setCmp([])} data-testid="inv-mgr-compare-clear">Kosongkan</button></div>
              <CompareTable list={list} {...shared} /></div>)}
          <p className="text-sm font-bold text-slate-700">Pengelola Perorangan terverifikasi ({o.candidates.length}) <span className="font-normal text-xs text-slate-500">· centang hingga {MAX_COMPARE} untuk dibandingkan</span></p>
          {!o.candidates.length ? <p className="text-xs text-slate-500" data-testid="inv-mgr-empty">Belum ada Pengelola Perorangan terverifikasi.</p> : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{o.candidates.map((c) => <CandidateCard key={c.id} c={c} checked={cmp.includes(c.id)} canAdd={cmp.length < MAX_COMPARE} onToggle={toggle} {...shared} />)}</div>)}
          <p className="text-xs text-slate-500">{o.responsibility}</p>
        </>)}
    </div>
  );
}
